import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

export const languages: Language[] = [
  { code: 'en', name: 'English',  nativeName: 'English',   flag: '🇬🇧', dir: 'ltr' },
  { code: 'ar', name: 'Arabic',   nativeName: 'العربية',   flag: '🇸🇦', dir: 'rtl' },
  { code: 'tr', name: 'Turkish',  nativeName: 'Türkçe',    flag: '🇹🇷', dir: 'ltr' },
  { code: 'de', name: 'German',   nativeName: 'Deutsch',   flag: '🇩🇪', dir: 'ltr' },
  { code: 'fr', name: 'French',   nativeName: 'Français',  flag: '🇫🇷', dir: 'ltr' },
  { code: 'es', name: 'Spanish',  nativeName: 'Español',   flag: '🇪🇸', dir: 'ltr' },
  { code: 'it', name: 'Italian',  nativeName: 'Italiano',  flag: '🇮🇹', dir: 'ltr' },
  { code: 'ru', name: 'Russian',  nativeName: 'Русский',   flag: '🇷🇺', dir: 'ltr' },
];

interface LanguageSwitcherProps {
  isScrolled?: boolean;
  isServicePage?: boolean;
}

export default function LanguageSwitcher({ isScrolled = false, isServicePage = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('preferredLanguage', languageCode);
    const selectedLang = languages.find(l => l.code === languageCode);
    if (selectedLang) {
      document.documentElement.dir = selectedLang.dir;
      document.documentElement.lang = languageCode;
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-amber-400/40 text-white/60 hover:text-amber-400 transition-all duration-200 cursor-pointer"
        style={{ fontFamily: "'Inter', sans-serif" }}
        aria-label="Select language"
      >
        <span className="text-base leading-none">{currentLanguage.flag}</span>
        <span className="text-xs font-medium tracking-widest uppercase hidden md:inline">
          {currentLanguage.code}
        </span>
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-[#111111] border border-white/10 overflow-hidden z-[200] min-w-[200px] shadow-2xl">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 transition-colors duration-200 cursor-pointer text-left ${
                language.code === currentLanguage.code
                  ? 'bg-amber-400/10 text-amber-400'
                  : 'text-white/50 hover:bg-white/5 hover:text-white/80'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="text-xl leading-none flex-shrink-0">{language.flag}</span>
              <span className="text-sm font-medium flex-1">{language.nativeName}</span>
              <span className="text-xs text-white/25 uppercase tracking-widest">{language.code}</span>
              {language.code === currentLanguage.code && (
                <i className="ri-check-line text-amber-400 text-sm flex-shrink-0"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
